import { useState } from 'react';

import { faDownload, faFile, faShare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNotification } from '../Contexts/NotificationContext';

const SupllyItem = ({ supply, loading }) => {
    const { setNotification } = useNotification();
    const [downloadLoading, setDownloadLoading] = useState(false);
    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleDownload = async (e) => {
        e.preventDefault();
        setDownloadLoading(true);

        try {
            const response = await fetch(`${baseUrl}/painel/materiais/${supply.produto_slug}/arquivos/${supply.id}/download`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Erro ao baixar arquivo: ${response.statusText}`);
            }

            const blob = await response.blob();
            const fileName = `${supply.nome}.${supply.extensao}`;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleShare = async () => {
        const shareUrl = `${window.location.origin}/arquivos/${supply.produto_slug}/materiais/${supply.id}`;
        const shareText = `Acesse esse material da Rometal: ${supply.nome}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Compartilhar',
                    text: shareText,
                    url: shareUrl,
                });
            } catch (error) {
                setNotification({
                    type: 'error',
                    message: 'Erro ao compartilhar',
                    show: true,
                });
            }
        } else {
            setNotification({
                type: 'alert',
                message: 'O compartilhamento não é suportado neste navegador.',
                show: true,
            });
        }
    };

    if (loading)
        return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 xl:gap-10 opacity-20 mt-10">
                {[...Array(4)].map((_, index) => (
                    <div
                        key={index}
                        role="status"
                        className="flex items-center justify-center h-0 pb-[90%] md:pb-[103%] max-w-sm bg-gray-300 rounded-lg animate-pulse dark:bg-gray-700"
                    >
                        <span className="sr-only">Loading...</span>
                    </div>
                ))}
            </div>
        );

    return (
        <div className="flex max-sm:flex-wrap max-sm:justify-center gap-6 items-start mb-10">
            {supply.preview ? (
                <img src={supply.preview} className="w-full max-w-[320px] rounded-xl border border-neutral-500" loading="lazy" />
            ) : (
                <div className="relative max-sm:w-2/3 w-full max-w-[320px] aspect-[32/43] bg-white rounded-xl border border-neutral-500">
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                        <span className="fa-layers fa-fw text-9xl">
                            <FontAwesomeIcon icon={faFile} className="text-secondary" />
                            <span className="fa-layers-text fa-inverse text-2xl font-black mt-2 uppercase">{supply.extensao}</span>
                        </span>
                    </div>
                </div>
            )}

            <div>
                <div className="mb-5">
                    <h5 className="text-white text-xs md:text-sm max-sm:text-center uppercase tracking-wide">Material</h5>
                    <h1 className="text-secondary text-2xl md:text-4xl max-sm:text-center font-semibold">{supply.nome}</h1>
                </div>

                <div className="mb-5">
                    <h5 className="text-white text-xs md:text-sm max-sm:text-center uppercase tracking-wide">Extensão</h5>
                    <h1 className="text-secondary md:text-2xl max-sm:text-center font-semibold">{supply.formato}</h1>
                </div>

                <div className="flex max-md:justify-center flex-wrap gap-4 mt-3 mb-20">
                    {supply.baixavel && (
                        <button
                            className="group flex items-center border-2 border-secondary rounded-md text-white font-semibold uppercase max-sm:text-sm sm:tracking-wide px-4 py-2 transition-all hover:bg-secondary disabled:opacity-60 disabled:cursor-not-allowed"
                            onClick={handleDownload}
                            disabled={downloadLoading}
                        >
                            {downloadLoading ? (
                                <svg
                                    className="animate-spin mr-2 h-4 w-4 text-secondary group-hover:text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                            ) : (
                                <FontAwesomeIcon icon={faDownload} className="mr-2 text-secondary transition-all group-hover:text-white" />
                            )}
                            {downloadLoading ? 'Baixando...' : 'Download'}
                        </button>
                    )}
                    {supply.compartilhavel && (
                        <button
                            className="group flex items-center border-2 border-secondary rounded-md text-white font-semibold uppercase max-sm:text-sm sm:tracking-wide px-4 py-2 transition-all hover:bg-secondary"
                            onClick={handleShare}
                        >
                            <FontAwesomeIcon icon={faShare} className="mr-2 text-secondary transition-all group-hover:text-white" />
                            Compartilhar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupllyItem;
